import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { useScrollPosition } from '@n8tb1t/use-scroll-position';

import { useConfig } from '@browsing/config';
import { trackSelectContent } from '@browsing/tracking';

import { Spinner } from '@client/components/shared/Shared';
import {
    getClientDeviceWidth,
    getIsMobileScreen,
} from '@client/redux/modules/clientDevice/selectors';
import { isSmScreen } from '@client/helpers/screens';
import { THB, ThbLinkType } from '@shared/types/mylounge';
import { getTabs, getImagesLoaded, getCategoryTabId } from '@client/redux/modules/mylounge';
import { CategoryTabIds } from '@shared/constants';
import { GridPageLayout } from '@lux/grid';
import { ErrorBoundary } from '@browsing/components';
import { findActiveTab, getTabImagesMap, buildUrlForThb, trackBannerClick } from './helpers';
import BannerSlider from './banner-slider';

import { SCROLL_TRACK_TOP_OFFSET } from './constants';

import { OuterWrapper, Wrapper, SpinnerWrapper } from './Banner.styles';

import { BirthdayVoucherConnected } from '../birthday-voucher/BirthdayVoucherConnected';
import { CommercialMessageContainer } from './CommercialMessages/commercial-message-container';
import { useGRE } from '../gre';

export const InternalHeroBanner = () => {
    const navigate = useNavigate();
    const config = useConfig();

    const clientWidth = useSelector(getClientDeviceWidth);
    const tabs = useSelector(getTabs);
    const imagesLoaded = useSelector(getImagesLoaded);

    const [scrollTracked, setScrollTracked] = useState(false);
    const categoryTabId = useSelector(getCategoryTabId);
    const [activeIndex, setActiveIndex] = useState(findActiveTab(tabs, categoryTabId));
    const [lastIndex, setLastIndex] = useState(null);

    const { shouldRenderGRECarousel, shouldRenderGRETabHeader, checkIfGRETab } = useGRE();

    const shouldRenderGRETreatment = shouldRenderGRECarousel || shouldRenderGRETabHeader;

    // load only active banner image
    const [tabImages, setTabImages] = useState(getTabImagesMap(tabs, activeIndex, false));

    const { thb } = tabs?.[activeIndex] || { thb: {} as THB };

    const trackScroll = useCallback(() => {
        if (
            !scrollTracked &&
            (!isSmScreen(clientWidth) || window.scrollY > SCROLL_TRACK_TOP_OFFSET)
        ) {
            setScrollTracked(true);
        }
    }, [scrollTracked, clientWidth]);

    useScrollPosition(
        () => {
            trackScroll();
        },
        [trackScroll, clientWidth],
        null,
        true,
        100,
    );

    // changing tab
    useEffect(() => {
        const newActiveIndex = findActiveTab(tabs, categoryTabId);
        if (newActiveIndex !== activeIndex) {
            setActiveIndex(newActiveIndex);
            setLastIndex(activeIndex);
            setTabImages(currentTabImages => ({
                ...currentTabImages,
                [newActiveIndex]: true,
            }));
            setScrollTracked(false);
        }
    }, [activeIndex, categoryTabId, tabs]);

    // unlock loading all banner images
    // TODO: move this logic inside the BannerSlider
    useEffect(() => {
        if (imagesLoaded) {
            setTabImages(getTabImagesMap(tabs, activeIndex, true));
        }
    }, [imagesLoaded, activeIndex, tabs]);

    const handleTrackBannerClick = useCallback(() => {
        trackBannerClick({
            thb,
            categoryTabId,
            activeIndex,
            isGA4Enabled: config.isEnabled('ga4'),
        });
    }, [thb, categoryTabId, activeIndex, config]);

    const navigateToThbTarget = useCallback(() => {
        if (!thb) {
            return;
        }

        handleTrackBannerClick();
        const url = buildUrlForThb(thb, categoryTabId);

        if (!url) {
            return;
        }

        if (thb.type === ThbLinkType.TAB) {
            navigate(url);
        } else {
            window.location.href = url;
        }
    }, [thb, handleTrackBannerClick, categoryTabId, navigate]);

    const changeActiveTab = useCallback(
        (newActiveIndex: number) =>
            navigate(`/event/${tabs?.[newActiveIndex]?.id}${window.location.search}`),
        [navigate, tabs],
    );

    const isSameTab = useCallback(
        (newActiveIndex: number) => newActiveIndex === activeIndex,
        [activeIndex],
    );

    const handleTabChange = useCallback(
        (newActiveIndex: number) => {
            if (isSameTab(newActiveIndex)) {
                return;
            }

            trackSelectContent('category_tab', 'swipe', { contentId: categoryTabId });

            changeActiveTab(newActiveIndex);
        },
        [isSameTab, changeActiveTab, categoryTabId],
    );

    const handleTabSwipe = useCallback(
        (newActiveIndex: number) => handleTabChange(newActiveIndex),
        [handleTabChange],
    );

    const isMainTab = categoryTabId === CategoryTabIds.ALL;

    const isActiveTabGRETab = useMemo(() => {
        const activeTab = tabs.find(tab => tab.id === categoryTabId);
        return checkIfGRETab(activeTab) ?? false;
    }, [tabs, categoryTabId, checkIfGRETab]);

    const isMobileScreen = useSelector(getIsMobileScreen);

    if (!tabs || !tabs.length || !tabs[activeIndex]) {
        return (
            <SpinnerWrapper>
                <Spinner black />
            </SpinnerWrapper>
        );
    }

    const isNoThbVariant = !thb;
    // @ts-expect-error - experimental field subject of A/B/C tests
    const isSmallThbVariant = Boolean(thb?.experimentalMedia);

    return (
        <OuterWrapper
            shouldRenderGRETreatment={shouldRenderGRETreatment}
            isGRETab={isActiveTabGRETab}
            id="thb-wrapper"
            $isNoThbVariant={isNoThbVariant}
        >
            <GridPageLayout>
                {!isMobileScreen && <BirthdayVoucherConnected />}
                <Wrapper
                    shouldRenderGRETreatment={shouldRenderGRETreatment}
                    isMainTab={isMainTab}
                    isGRETab={isActiveTabGRETab}
                    $isNoThbVariant={isNoThbVariant}
                    $isSmallThbVariant={isSmallThbVariant}
                >
                    <CommercialMessageContainer />
                    <BannerSlider
                        tabs={tabs}
                        activeIndex={activeIndex}
                        lastIndex={lastIndex}
                        onBannerSwipe={handleTabSwipe}
                        tabImages={tabImages}
                        onImageClick={navigateToThbTarget}
                    />
                </Wrapper>
            </GridPageLayout>
        </OuterWrapper>
    );
};

export const HeroBanner: React.FC = () => (
    <ErrorBoundary componentName="MyLoungeBanner">
        <InternalHeroBanner />
    </ErrorBoundary>
);
